/**
 * Warm-loads the lazy PDF chunk so a tab left open across a deploy can still
 * download a handout.
 *
 * Every deploy renames the PDF chunk (its hash covers the main bundle it
 * imports), and Cloudflare Pages answers a request for the old name with
 * index.html. A tab opened before the deploy then fails its first download.
 * Importing the chunk shortly after the page loads puts it in the tab's module
 * map, so the later download needs no network at all for the code.
 *
 * Kept out of main.ts so the caching rules are unit-testable.
 */

export interface PdfLoader<T> {
  /** Start loading in the background. Errors are swallowed; load() retries. */
  warm(): void;
  /** The loaded module. A failed earlier attempt is retried, not replayed. */
  load(): Promise<T>;
}

export function createPdfLoader<T>(importer: () => Promise<T>): PdfLoader<T> {
  let pending: Promise<T> | null = null;

  function load(): Promise<T> {
    if (pending === null) {
      pending = importer().catch((err: unknown) => {
        // Forget the failure so the next call imports again (a dropped
        // connection during warm-up must not poison the real download).
        pending = null;
        throw err;
      });
    }
    return pending;
  }

  function warm(): void {
    load().catch(() => undefined);
  }

  return { warm, load };
}

/**
 * Run `task` once the page has finished loading and the main thread is idle,
 * so warm-up never competes with first paint or the DC's first keystrokes.
 * Safari before 18 has no requestIdleCallback; a short timeout stands in.
 */
export function whenIdle(task: () => void, win: Window = window): void {
  const schedule = (): void => {
    if (typeof win.requestIdleCallback === "function") {
      win.requestIdleCallback(task, { timeout: 5000 });
    } else {
      win.setTimeout(task, 1500);
    }
  };
  if (win.document.readyState === "complete") schedule();
  else win.addEventListener("load", schedule, { once: true });
}
